"use strict"

import React, { useCallback, useEffect, useMemo, useState, useContext } from "react"

import { AppSettingConfigContext } from "./AppSettingsConfigProvider"
import { logToServer } from "../ErrorBoundary"
import { addNewObject, getObjectsList, updateObject, deleteObject } from "../Loaders"
import { parseDate } from "../shared_components/utils"

export const OAConfigContext = React.createContext({})

export function withOperatorActionConfig(Component) {
  return function (props) {
    return (
      <OAConfigContext.Consumer>
        {(configData) => <Component {...props} {...configData} />}
      </OAConfigContext.Consumer>
    )
  }
}

export default function OperatorActionConfigProvider(props) {
  const appConfig = useContext(AppSettingConfigContext)
  const { setNewActionRuns, newActionRuns, activeUsers = [] } = appConfig

  const [reactors, setReactors] = useState([])
  const [actions, setActions] = useState({})
  const [actors, setActors] = useState({})
  const [loading, setLoading] = useState(true)
  const [newActionErr, setNewActionErr] = useState(null)
  const [editActionErr, setEditActionErr] = useState(null)
  const [deleteActionErr, setDeleteActionErr] = useState(null)

  const getReactorActions = useCallback((cutoff = 20) => {
    let url = "/warehouse/sources/reactor/actions/"
    if (cutoff) {
      url = url + `?cutoff=${cutoff}`
    }
    setLoading(true)

    getObjectsList(url)
      .then((reactorActions) => {
        let actions = {}
        reactorActions.forEach((reactor) => {
          actions[reactor.name] = reactor.actions.map((act) => ({
            ...act,
            timestamp: act.timestamp && parseDate(act.timestamp),
          }))
        })
        setReactors(reactorActions.map((reactor) => reactor.name))
        setActions(actions)
        setLoading(false)
      })
      .catch((err) => {
        console.error(err)
        logToServer(err)
        setLoading(false)
      })
  }, [])

  useEffect(() => {
    getReactorActions()
  }, [])
  useEffect(() => {
    setActors(Object.fromEntries(activeUsers.map((user) => [user.username, user])))
  }, [activeUsers])

  const createUpdateOrDeleteAction = (commentData, requestType, onComplete = () => {}) => {
    let loaderMethod, errMethod

    if (requestType === "update") {
      loaderMethod = () => updateObject(`/warehouse/actions/${commentData.id}/`, commentData)
      errMethod = setEditActionErr
    } else if (requestType === "create") {
      loaderMethod = () => addNewObject(`/warehouse/actions/`, commentData)
      errMethod = setNewActionErr
    } else {
      loaderMethod = () => deleteObject(`/warehouse/actions/${commentData.id}/`)
      errMethod = setDeleteActionErr
    }

    loaderMethod()
      .then((resp) => {
        const run_ids = Array.isArray(resp) ? resp.map((r) => r.run) : [resp.run]
        getReactorActions()
        setNewActionRuns &&
          !newActionRuns.includes(resp.run) &&
          setNewActionRuns([...newActionRuns, ...run_ids])
        onComplete()
      })
      .catch((err) => {
        console.error(err)
        logToServer(err)
        errMethod(err)

        // Set time out for error message
        if (["update", "create"].includes(requestType)) {
          setTimeout(() => {
            errMethod(null)
            onComplete()
          }, 9000)
        } else {
          errMethod(null)
          onComplete()
        }
      })
  }

  const value = useMemo(
    () => ({
      loading: loading,
      reactors: reactors,
      actions: actions,
      actors: actors,
      newActionErr: newActionErr,
      editActionErr: editActionErr,
      deleteActionErr: deleteActionErr,
      createUpdateOrDeleteAction: createUpdateOrDeleteAction,
    }),
    [loading, reactors, actions, actors, newActionErr, editActionErr, deleteActionErr],
  )

  return <OAConfigContext.Provider value={value}>{props.children}</OAConfigContext.Provider>
}
